import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { useAuth } from '@/utils/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { BUILDER_ENABLED, METHODOLOGIES_ENABLED, SITE_AUDIT_ENABLED, TOOLS_ENABLED } from '@/utils/config';
import AppLayout from '@/components/layout/AppLayout';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import EmailVerificationPage from '@/pages/EmailVerificationPage';
import DashboardPage from '@/pages/DashboardPage';
import MarketAnalysisPage from '@/pages/MarketAnalysisPage';
import PersonasPage from '@/pages/PersonasPage';
import PositioningPage from '@/pages/PositioningPage';
import BusinessModelPage from '@/pages/BusinessModelPage';
import MvpPlanPage from '@/pages/MvpPlanPage';
import MarketingStrategyPage from '@/pages/MarketingStrategyPage';
import BuilderListPage from '@/pages/BuilderListPage';
import BuilderEditorRoute from '@/pages/BuilderEditorRoute';
import DeckViewPage from '@/pages/DeckViewPage';
import PublicDeckPage from '@/pages/PublicDeckPage';
import SiteAuditListPage from '@/pages/SiteAuditListPage';
import SiteAuditReportPage from '@/pages/SiteAuditReportPage';
import ToolsPage from '@/pages/ToolsPage';
import FinancialModelPage from '@/pages/FinancialModelPage';
import MethodsCatalogPage from '@/pages/MethodsCatalogPage';
import MethodPage from '@/pages/MethodPage';
import ResearchPage from '@/pages/ResearchPage';
import ProfilePage from '@/pages/ProfilePage';
import SubscriptionPage from '@/pages/SubscriptionPage';
import ChatPage from '@/pages/ChatPage';
import HelpPage from '@/pages/HelpPage';
import { YandexMetrika } from '@/components/shared/YandexMetrika';
import {
  clearCourseClaimStatus, isCourseClaimHandoff, readCourseClaimStatus, storeCourseClaimStatus,
} from '@/utils/course-claim';

/**
 * Picks up where `POST /api/course-entitlement/handoff` left off: the backend
 * redirected here with `?course_claim=<status>`. This component only routes — the
 * status is stored, and the student is taken to the subscription page, where a refusal
 * is explained and a waiting claim is offered for them to accept or decline. Nothing is
 * redeemed on the way; see useCourseClaimPrompt for why that matters.
 *
 * Both cases wait for the student to be signed in: before that there is no page of
 * theirs to say it on, and `/subscription` would bounce them to the login page.
 */
function CourseClaimBridge() {
  const { isAuthenticated } = useAuth();
  const { localizedPath } = useLanguage();
  const navigate = useNavigate();
  // The trip is made once per mount, even if this component re-renders.
  const routed = useRef(false);

  useLayoutEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('course_claim');
    if (!isCourseClaimHandoff(status)) return;
    // Out of the address bar before anything else: the status has been read, and a
    // reloaded or shared URL should not replay it.
    params.delete('course_claim');
    const query = params.toString();
    window.history.replaceState(
      window.history.state, '',
      window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
    );
    storeCourseClaimStatus(status);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || routed.current) return;
    const status = readCourseClaimStatus();
    if (!status) return;
    routed.current = true;

    // A refusal is over and done with, so it travels in navigation state and the stored
    // copy goes. A pending claim stays stored: the subscription page reads it, and it
    // has to survive a reload while the student is deciding.
    if (status !== 'pending') clearCourseClaimStatus();

    // After this commit's effects have run: a <Navigate> that is redirecting at this
    // very moment — the login page sending a freshly signed-in student on to the
    // dashboard — would otherwise land last and drop this on the floor.
    queueMicrotask(() => {
      navigate(localizedPath('/subscription'), {
        replace: true,
        state: status === 'pending' ? {} : { courseClaimResult: status },
      });
    });
  }, [isAuthenticated, navigate, localizedPath]);
  return null;
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation('common');
  const { localizedPath } = useLanguage();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-muted-foreground">{t('loading')}</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={localizedPath('/login')} replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation('common');
  const { localizedPath } = useLanguage();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-muted-foreground">{t('loading')}</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={localizedPath('/dashboard')} replace />;
  }

  return <>{children}</>;
}

/** Shared route tree — used for both /ru (default) and /en prefix */
function appRoutes(dashboardRedirect: string) {
  return (
    <>
      {/* Public routes */}
      <Route path="login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
      <Route path="verify-email" element={<EmailVerificationPage />} />

      {/* Private routes with shared layout (Sidebar persists) */}
      <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="market-analysis" element={<MarketAnalysisPage />} />
        <Route path="personas" element={<PersonasPage />} />
        <Route path="positioning" element={<PositioningPage />} />
        <Route path="business-model" element={<BusinessModelPage />} />
        <Route path="financial-model" element={<FinancialModelPage />} />
        <Route path="mvp-plan" element={<MvpPlanPage />} />
        <Route path="marketing-strategy" element={<MarketingStrategyPage />} />
        {METHODOLOGIES_ENABLED && <Route path="methods" element={<MethodsCatalogPage />} />}
        {METHODOLOGIES_ENABLED && <Route path="methods/:methodId" element={<MethodPage />} />}
        {BUILDER_ENABLED && <Route path="builder" element={<BuilderListPage />} />}
        {BUILDER_ENABLED && <Route path="builder/:buildId" element={<BuilderEditorRoute />} />}
        {BUILDER_ENABLED && <Route path="builder/:buildId/view" element={<DeckViewPage />} />}
        {SITE_AUDIT_ENABLED && <Route path="site-audit" element={<SiteAuditListPage />} />}
        {SITE_AUDIT_ENABLED && <Route path="site-audit/:auditId" element={<SiteAuditReportPage />} />}
        {TOOLS_ENABLED && <Route path="tools" element={<ToolsPage />} />}
        <Route path="research" element={<ResearchPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="payment/success" element={<SubscriptionPage />} />
        <Route path="payment/fail" element={<SubscriptionPage />} />
      </Route>

      {/* Default redirect */}
      <Route index element={<Navigate to={dashboardRedirect} replace />} />
      <Route path="*" element={<Navigate to={dashboardRedirect} replace />} />
    </>
  );
}

export default function App() {
  return (
    // Без key: ключ по pathname пересоздавал всё дерево роутов на каждой навигации,
    // из-за чего заново монтировался AppLayout (сбрасывалось его состояние, а счётчик
    // визитов для формы обратной связи рос на каждый переход). Сброс после ошибки
    // делает вложенный ErrorBoundary в AppLayout — он и завязан на pathname.
    <ErrorBoundary>
      <CourseClaimBridge />
      <YandexMetrika />
      <Routes>
        {/* Публичная ссылка на презентацию: без оболочки приложения, без языкового
            префикса и без авторизации — её открывает инвестор, у которого нет аккаунта. */}
        {BUILDER_ENABLED && <Route path="/p/deck/:buildId" element={<PublicDeckPage />} />}

        {/* EN routes with /en prefix */}
        <Route path="/en">
          {appRoutes('/en/dashboard')}
        </Route>

        {/* RU routes (default, no prefix) */}
        <Route path="/">
          {appRoutes('/dashboard')}
        </Route>
      </Routes>
    </ErrorBoundary>
  );
}
